import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Loader2,
  CreditCard,
  Lock,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCreateChargeMutation, useGetPaymentMethodQuery, useGetBillingHistoryQuery } from './billingApi';
import { useAuth } from '../../../context/AuthContext';
import { PLANS } from '../consts';

/* ─── Helpers ─────────────────────────────────────────────── */

const OPENPAY_MERCHANT_ID = process.env.REACT_APP_OPENPAY_MERCHANT_ID || '';
const OPENPAY_PUBLIC_KEY  = process.env.REACT_APP_OPENPAY_PUBLIC_KEY  || '';
const OPENPAY_SANDBOX     = process.env.REACT_APP_OPENPAY_SANDBOX !== 'false';

function formatCardNumber(value) {
  return value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(value) {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

function detectBrand(number) {
  const n = number.replace(/\s/g, '');
  if (/^4/.test(n)) return 'visa';
  if (/^5[1-5]/.test(n)) return 'mastercard';
  if (/^3[47]/.test(n)) return 'amex';
  return null;
}

const brandColors = {
  visa:       'from-blue-700 to-blue-500',
  mastercard: 'from-red-600 to-orange-400',
  amex:       'from-green-700 to-green-500',
};

const brandLabel = { visa: 'VISA', mastercard: 'MC', amex: 'AMEX' };

function CardBrandBadge({ brand }) {
  if (!brand) return null;
  return (
    <div className={`w-12 h-8 rounded bg-gradient-to-r ${brandColors[brand] ?? 'from-slate-600 to-slate-400'} flex items-center justify-center text-white text-[10px] font-black tracking-wider`}>
      {brandLabel[brand] ?? brand.toUpperCase()}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: { label: 'Pagado', variant: 'default', className: 'bg-green-500/15 text-green-500 border-green-500/30' },
    failed:    { label: 'Fallido', variant: 'destructive', className: '' },
    pending:   { label: 'Pendiente', variant: 'outline', className: '' },
    refunded:  { label: 'Reembolsado', variant: 'outline', className: 'text-amber-500 border-amber-500/30' },
  };
  const s = map[status] ?? { label: status, variant: 'outline', className: '' };
  return <Badge variant={s.variant} className={s.className}>{s.label}</Badge>;
}

/* ─── OpenPay SDK loader ──────────────────────────────────── */
function useOpenPay() {
  const [ready, setReady] = useState(false);
  const [deviceSessionId, setDeviceSessionId] = useState('');

  useEffect(() => {
    if (!OPENPAY_MERCHANT_ID || !OPENPAY_PUBLIC_KEY) return;

    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

    Promise.all([
      loadScript('https://js.openpay.mx/openpay.v1.min.js'),
      loadScript('https://js.openpay.mx/openpay-data.v1.min.js'),
    ])
      .then(() => {
        window.OpenPay.setId(OPENPAY_MERCHANT_ID);
        window.OpenPay.setApiKey(OPENPAY_PUBLIC_KEY);
        window.OpenPay.setSandboxMode(OPENPAY_SANDBOX);
        const sid = window.OpenPay.deviceData.setup();
        setDeviceSessionId(sid);
        setReady(true);
      })
      .catch(() => toast.error('No se pudo cargar el SDK de OpenPay'));
  }, []);

  return { ready, deviceSessionId };
}

/* ─── Card Form ───────────────────────────────────────────── */
function CardForm({ onTokenized, loading }) {
  const [form, setForm] = useState({
    holderName: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
  });
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState('');

  const brand = detectBrand(form.cardNumber);

  function validate() {
    const e = {};
    if (!form.holderName.trim()) e.holderName = 'Nombre requerido';
    const digits = form.cardNumber.replace(/\s/g, '');
    if (digits.length < 15) e.cardNumber = 'Número de tarjeta inválido';
    if (form.expiry.length < 5) e.expiry = 'Fecha inválida';
    if (form.cvv.length < 3) e.cvv = 'CVV inválido';
    return e;
  }

  function handleChange(field, rawValue) {
    let value = rawValue;
    if (field === 'cardNumber') value = formatCardNumber(rawValue);
    if (field === 'expiry') value = formatExpiry(rawValue);
    if (field === 'cvv') value = rawValue.replace(/\D/g, '').slice(0, 4);
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const [expMonth, expYear] = form.expiry.split('/');
    onTokenized({
      cardNumber: form.cardNumber.replace(/\s/g, ''),
      holderName: form.holderName,
      expMonth,
      expYear,
      cvv: form.cvv,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="openpay-card-form">
      {/* Card preview strip */}
      <div className="relative h-36 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 p-5 select-none">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 80% 20%, #fff 0%, transparent 60%)'
        }} />
        <div className="flex justify-between items-start">
          <div className="w-10 h-7 rounded bg-yellow-400/90 grid grid-cols-2 gap-px p-0.5">
            {[...Array(4)].map((_, i) => <div key={i} className="rounded-sm bg-yellow-600/40" />)}
          </div>
          {brand && <CardBrandBadge brand={brand} />}
        </div>
        <p className="mt-4 font-mono text-white/90 text-base tracking-widest truncate">
          {form.cardNumber || '•••• •••• •••• ••••'}
        </p>
        <div className="flex justify-between mt-2">
          <div>
            <p className="text-white/40 text-[9px] uppercase tracking-widest">Titular</p>
            <p className="text-white/80 text-xs font-medium truncate max-w-[160px]">
              {form.holderName || 'NOMBRE APELLIDO'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-white/40 text-[9px] uppercase tracking-widest">Vence</p>
            <p className="text-white/80 text-xs font-medium">{form.expiry || 'MM/AA'}</p>
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-2">
        <Label htmlFor="holderName">Nombre del titular</Label>
        <Input
          id="holderName"
          value={form.holderName}
          onChange={(e) => handleChange('holderName', e.target.value.toUpperCase())}
          onFocus={() => setFocused('holderName')}
          onBlur={() => setFocused('')}
          placeholder="COMO APARECE EN LA TARJETA"
          autoComplete="cc-name"
          data-testid="card-holder-input"
          className={errors.holderName ? 'border-destructive' : ''}
        />
        {errors.holderName && <p className="text-xs text-destructive">{errors.holderName}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="cardNumber">Número de tarjeta</Label>
        <div className="relative">
          <Input
            id="cardNumber"
            value={form.cardNumber}
            onChange={(e) => handleChange('cardNumber', e.target.value)}
            onFocus={() => setFocused('cardNumber')}
            onBlur={() => setFocused('')}
            placeholder="1234 5678 9012 3456"
            autoComplete="cc-number"
            inputMode="numeric"
            data-testid="card-number-input"
            className={`pr-12 font-mono ${errors.cardNumber ? 'border-destructive' : ''}`}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {brand ? <CardBrandBadge brand={brand} /> : <CreditCard className="w-5 h-5 text-muted-foreground" />}
          </div>
        </div>
        {errors.cardNumber && <p className="text-xs text-destructive">{errors.cardNumber}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="expiry">Fecha de vencimiento</Label>
          <Input
            id="expiry"
            value={form.expiry}
            onChange={(e) => handleChange('expiry', e.target.value)}
            placeholder="MM/AA"
            inputMode="numeric"
            autoComplete="cc-exp"
            data-testid="expiry-input"
            className={`font-mono ${errors.expiry ? 'border-destructive' : ''}`}
          />
          {errors.expiry && <p className="text-xs text-destructive">{errors.expiry}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="cvv">CVV</Label>
          <Input
            id="cvv"
            type="password"
            value={form.cvv}
            onChange={(e) => handleChange('cvv', e.target.value)}
            placeholder="•••"
            inputMode="numeric"
            autoComplete="cc-csc"
            data-testid="cvv-input"
            className={`font-mono ${errors.cvv ? 'border-destructive' : ''}`}
          />
          {errors.cvv && <p className="text-xs text-destructive">{errors.cvv}</p>}
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-primary hover:bg-primary/90 h-11"
        data-testid="submit-charge-btn"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</>
        ) : (
          <><Lock className="w-4 h-4 mr-2" /> Guardar y procesar pago</>
        )}
      </Button>

      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
        Pago seguro vía OpenPay · Datos cifrados con TLS · PCI DSS Nivel 1
      </p>
    </form>
  );
}

/* ─── Main Component ─────────────────────────────────────── */
export default function BillingTab({ currentPlan }) {
  const { user } = useAuth();
  const { ready: sdkReady, deviceSessionId } = useOpenPay();

  const { data: paymentMethod, isLoading: pmLoading } = useGetPaymentMethodQuery();
  const { data: history = [], isLoading: historyLoading } = useGetBillingHistoryQuery();
  const [createCharge, { isLoading: chargeLoading }] = useCreateChargeMutation();

  const [showForm, setShowForm] = useState(false);
  const activePlan = PLANS.find(p => p.id === currentPlan);

  async function handleTokenized({ cardNumber, holderName, expMonth, expYear, cvv }) {
    if (!sdkReady) {
      toast.error('SDK de OpenPay no disponible. Recarga la página.');
      return;
    }

    // Tokenize card data in the browser using OpenPay JS SDK
    // Card number NEVER reaches our server
    window.OpenPay.token.create(
      {
        card_number: cardNumber,
        holder_name: holderName,
        expiration_month: expMonth,
        expiration_year: expYear,
        cvv2: cvv,
      },
      async (response) => {
        const tokenId = response.data?.id;
        if (!tokenId) {
          toast.error('No se pudo tokenizar la tarjeta');
          return;
        }
        try {
          await createCharge({
            token_id: tokenId,
            device_session_id: deviceSessionId,
            plan: currentPlan || 'starter',
            holder_name: holderName,
          }).unwrap();
          toast.success('Pago procesado correctamente');
          setShowForm(false);
        } catch (err) {
          toast.error(err?.data?.message || 'El pago fue rechazado');
        }
      },
      (error) => {
        const msg = error?.data?.description || 'Error al procesar la tarjeta';
        toast.error(msg);
      },
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Payment Method */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg">Método de pago</CardTitle>
              <CardDescription>Tarjeta registrada para facturación</CardDescription>
            </div>
            {!OPENPAY_MERCHANT_ID && (
              <Badge variant="outline" className="text-amber-500 border-amber-500/30 text-xs">
                Config. pendiente
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {pmLoading ? (
            <div className="h-16 rounded-xl bg-muted/40 animate-pulse" />
          ) : paymentMethod?.last4 ? (
            <div className="flex items-center justify-between p-4 rounded-xl border bg-muted/20 hover:bg-muted/40 transition-colors">
              <div className="flex items-center gap-4">
                <CardBrandBadge brand={paymentMethod.brand} />
                <div>
                  <p className="font-medium">
                    {paymentMethod.brand?.charAt(0).toUpperCase() + paymentMethod.brand?.slice(1) || 'Tarjeta'}{' '}
                    terminada en <span className="font-mono">{paymentMethod.last4}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Vence {paymentMethod.expiry_month}/{paymentMethod.expiry_year} · {paymentMethod.holder}
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="text-green-500 border-green-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Activa
              </Badge>
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 gap-3 text-center text-muted-foreground">
              <CreditCard className="w-10 h-10 opacity-30" />
              <p className="text-sm">No hay método de pago registrado</p>
            </div>
          )}

          {activePlan && (
            <>
              <Separator />
              <div className="flex items-center justify-between p-4 rounded-xl bg-primary/5 border border-primary/20">
                <div>
                  <p className="text-sm text-muted-foreground">Plan activo</p>
                  <p className="font-heading font-bold">{activePlan.name} Plan</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-heading font-bold">${activePlan.price}</p>
                  <p className="text-xs text-muted-foreground">MXN / mes</p>
                </div>
              </div>
            </>
          )}

          <Button
            onClick={() => setShowForm(!showForm)}
            variant={showForm ? 'outline' : 'default'}
            className={showForm ? '' : 'bg-primary hover:bg-primary/90'}
            data-testid="toggle-card-form-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {showForm ? 'Cancelar' : paymentMethod?.last4 ? 'Actualizar tarjeta' : 'Agregar tarjeta'}
          </Button>

          {showForm && (
            <>
              <Separator />
              {!sdkReady ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cargando SDK de OpenPay...
                </div>
              ) : (
                <CardForm onTokenized={handleTokenized} loading={chargeLoading} />
              )}
              {!OPENPAY_MERCHANT_ID && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-600">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Las variables <code>REACT_APP_OPENPAY_MERCHANT_ID</code> y <code>REACT_APP_OPENPAY_PUBLIC_KEY</code> no están configuradas.
                    El formulario no funcionará hasta agregarlas al <code>.env</code>.
                  </span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Historial de pagos</CardTitle>
          <CardDescription>Transacciones procesadas a través de OpenPay</CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-muted/40 animate-pulse" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-2 text-center text-muted-foreground">
              <CreditCard className="w-8 h-8 opacity-30" />
              <p className="text-sm">Sin transacciones registradas</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Tarjeta</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((tx) => (
                  <TableRow key={tx.id} data-testid={`tx-row-${tx.id}`}>
                    <TableCell className="text-sm">
                      {new Date(tx.created_at).toLocaleDateString('es-MX', {
                        year: 'numeric', month: 'short', day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell className="text-sm">{tx.description || `${tx.plan} Plan`}</TableCell>
                    <TableCell>
                      {tx.card_last4 ? (
                        <div className="flex items-center gap-2">
                          {tx.card_brand && <CardBrandBadge brand={tx.card_brand} />}
                          <span className="font-mono text-sm">••••{tx.card_last4}</span>
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium font-mono">
                      ${Number(tx.amount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={tx.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
